/**
 * O que as telas do atendimento compartilham (spec 157): o horário em português,
 * a lista de frases como texto, e o mapa de erro → campo.
 *
 * `descreverHorario` repete `handoff/schedule.descrever` do backend de propósito:
 * a prévia que a pessoa lê na tela tem que ser a MESMA frase que o cliente recebe,
 * e ela sai das janelas, nunca de um texto livre (FR-017).
 */

export interface JanelaDeAtendimento {
  dias: number[]
  de: string
  ate: string
}

export interface HorarioDeAtendimento {
  timezone: string
  janelas: JanelaDeAtendimento[]
}

const NOMES: Record<number, string> = {
  1: 'segunda',
  2: 'terça',
  3: 'quarta',
  4: 'quinta',
  5: 'sexta',
  6: 'sábado',
  7: 'domingo',
}

/** "segunda a sexta, 08:00 às 18:00", como o backend escreve. */
export function descreverHorario(horario?: HorarioDeAtendimento | null): string | null {
  if (!horario || !horario.janelas?.length) return null
  const partes: string[] = []
  for (const janela of horario.janelas) {
    const dias = [...(janela.dias ?? [])].sort((a, b) => a - b)
    if (!dias.length) continue
    const continua = dias.every((d, i) => i === 0 || d === dias[i - 1] + 1)
    const rotulo =
      continua && dias.length > 1
        ? `${NOMES[dias[0]]} a ${NOMES[dias[dias.length - 1]]}`
        : dias.map((d) => NOMES[d] ?? String(d)).join(', ')
    partes.push(`${rotulo}, ${janela.de} às ${janela.ate}`)
  }
  return partes.length ? partes.join('; ') : null
}

/** A mensagem de fora do horário como o cliente lê: `{horario}` vira as janelas. */
export function preencherHorario(
  mensagem: string | null | undefined,
  horario?: HorarioDeAtendimento | null
): string {
  return (mensagem ?? '').replace('{horario}', descreverHorario(horario) ?? '').trim()
}

/** FR-018: a lista de frases vira texto, um item por linha; linhas vazias saem. */
export function listaParaTexto(itens?: unknown): string {
  return Array.isArray(itens) ? itens.map(String).join('\n') : ''
}

export function textoParaLista(texto: string): string[] {
  return texto
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
}

export interface ErroDeCampo {
  loc: (string | number)[]
  msg: string
}

/** O erro do servidor no formato do 422, mapeado para o campo (`loc` unida por ponto). */
export function errosPorCampo(detalhe: unknown): {
  porCampo: Record<string, string>
  lista: ErroDeCampo[]
} {
  const lista: ErroDeCampo[] = []
  if (Array.isArray(detalhe)) {
    for (const bruto of detalhe) {
      if (bruto && typeof bruto === 'object' && 'msg' in bruto) {
        const e = bruto as { loc?: unknown; msg?: unknown }
        lista.push({
          loc: Array.isArray(e.loc) ? (e.loc as (string | number)[]) : [],
          msg: String(e.msg ?? 'Valor inválido.'),
        })
      }
    }
  } else if (typeof detalhe === 'string') {
    lista.push({ loc: [], msg: detalhe })
  }
  const porCampo: Record<string, string> = {}
  for (const e of lista) {
    const chave = e.loc.join('.')
    if (chave && !porCampo[chave]) porCampo[chave] = e.msg
  }
  return { porCampo, lista }
}

/** Quem pode gravar: `owner`/`admin` da organização, ou o operador da plataforma. */
export function podeEditarAtendimento(
  organizacao?: { user_role?: string | null } | null,
  usuario?: { is_superadmin?: boolean } | null
): boolean {
  if (usuario?.is_superadmin) return true
  const papel = (organizacao?.user_role ?? '').toLowerCase()
  return papel === 'owner' || papel === 'admin'
}

/** O caminho da seção da organização, para o link do campo travado (FR-007). */
export function linkDaOrganizacao(secao: string): string {
  return `/settings?tab=atendimento#${secao}`
}
